import { EmptyState, Spinner } from '../../components';
import { ClipboardCheckIcon } from '../../layout/icons';
import { vnDate, type SubmissionTask } from './submissions';
import { useClassSubmissions } from './useClassSubmissions';
import '../../styles/submissions.css';

export interface SubmissionsPanelProps {
  classTitle: string;
  /** Opens the task's đề as a tab, its Kết quả beside it. */
  onOpen: (task: SubmissionTask, courseTitle: string) => void;
  /** The task whose tab is on the board. */
  activeTaskId?: string | null;
}

// Bài tập: what this period's class handed in on IZTeach; a row puts its đề on the board with the results
export function SubmissionsPanel({ classTitle, onOpen, activeTaskId }: SubmissionsPanelProps) {
  const { roster, rosterLoading, loading, error, tasks, courseTitles, reload } = useClassSubmissions();

  if (roster.length === 0) {
    if (rosterLoading) return <Spinner label="Đang tải danh sách lớp…" />;
    return <EmptyState icon={<ClipboardCheckIcon size={26} />} title="Chưa có danh sách lớp" description="Bảng chưa tải được danh sách học sinh của tiết này nên chưa tìm được bài nộp." />;
  }
  if (loading && tasks.length === 0) return <Spinner label="Đang tìm bài nộp của lớp…" />;
  if (error) return <EmptyState icon={<ClipboardCheckIcon size={26} />} title="Chưa tải được bài nộp" description={error} action={<button type="button" className="secondary-button" onClick={reload}>Thử lại</button>} />;
  if (tasks.length === 0) return <EmptyState icon={<ClipboardCheckIcon size={26} />} title="Chưa có bài nộp nào" description={`Học sinh lớp ${classTitle || 'này'} chưa nộp bài tập về nhà nào trên IZTeach.`} action={<button type="button" className="secondary-button" onClick={reload}>Tải lại</button>} />;

  const years: string[] = [];
  tasks.forEach((item) => { if (years.indexOf(item.schoolYear) < 0) years.push(item.schoolYear); });
  return (
    <div className="hw-panel">
      <div className="hw-panel__bar">
        <p>Bài tập học sinh lớp {classTitle || 'này'} đã nộp trên IZTeach.</p>
        <button type="button" className="hw-panel__refresh" disabled={loading} onClick={reload}>{loading ? 'Đang tải…' : 'Tải lại'}</button>
      </div>
      {years.map((year) => (
        <section key={year || 'none'} className="hw-panel__year">
          <h3>{year ? `Năm học ${year}` : 'Chưa rõ ngày nộp'}</h3>
          <ul className="up-list">
            {tasks.filter((item) => item.schoolYear === year).map((item) => (
              <li key={item.id}>
                <button type="button" className={item.id === activeTaskId ? 'up-list__row is-selected' : 'up-list__row'} aria-current={item.id === activeTaskId ? 'true' : undefined} onClick={() => onOpen(item, courseTitles[item.courseId] || '')}>
                  <span className="up-list__mark" aria-hidden={true}><ClipboardCheckIcon size={18} /></span>
                  <span className="up-list__text">
                    <span className="up-list__name" title={item.title}>{item.title}</span>
                    <span className="up-list__meta">{[`${item.count}/${roster.length} đã nộp`, item.latestAt ? `mới nhất ${vnDate(item.latestAt)}` : ''].filter(Boolean).join(' • ')}</span>
                    {courseTitles[item.courseId] ? <span className="up-list__meta" title={courseTitles[item.courseId]}>{courseTitles[item.courseId]}</span> : null}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
